"use client";

import { ChevronsDown } from "lucide-react";
import React, { useState, useRef, useEffect } from "react";
import { Button } from "../ui/button";
import { cn } from "@/lib/utils";

interface ReadMoreProps {
  text?: string;
  minHeight: string; // Use string for CSS height values like '100px' or '5em'
  buttonText?: string;
  className?: string;
}

export const ReadMore: React.FC<ReadMoreProps> = ({
  text,
  minHeight,
  buttonText = "Read More",
  className,
  ...props
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);

  // Check if content overflows on mount and when text changes
  useEffect(() => {
    if (textRef.current) {
      const actualHeight = textRef.current.scrollHeight;
      const minHeightValue = parseInt(minHeight, 10); // Assuming minHeight is in px for comparison

      // Check if actual height exceeds minHeight
      if (actualHeight > minHeightValue) {
        setIsOverflowing(true);
      } else {
        setIsOverflowing(false);
      }
    }
  }, [text, minHeight]); // Re-run effect if text or minHeight changes

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div {...props}>
      <div
        ref={textRef}
        style={{
          maxHeight: isExpanded ? "none" : minHeight,
          overflow: "hidden",
          maskImage:
            isOverflowing && !isExpanded
              ? "linear-gradient(to bottom, black 50%, transparent 100%)"
              : "none",
          WebkitMaskImage:
            isOverflowing && !isExpanded
              ? "linear-gradient(to bottom, black 50%, transparent 100%)"
              : "none",
        }}
      >
        <div
          className={cn(
            "[&_h1]:mb-4 [&_h1]:text-2xl [&_h1]:font-bold [&_h2]:mb-3 [&_h2]:text-xl [&_h2]:font-bold [&_h3]:mb-3 [&_h3]:text-lg [&_h3]:font-bold [&_li]:mb-2 [&_p]:mb-4 [&_ul]:mb-6 [&_ul]:list-disc [&_ul]:pl-6",
            className
          )}
          dangerouslySetInnerHTML={{ __html: text ?? "" }}
        />
      </div>
      <div className="text-center">
        {isOverflowing && !isExpanded && (
          <Button variant="link" onClick={handleToggle}>
            {buttonText} <ChevronsDown />
          </Button>
        )}
        {isOverflowing && isExpanded && (
          <Button variant="link" onClick={handleToggle}>
            Read Less <ChevronsDown className="rotate-180" />
          </Button>
        )}
      </div>
    </div>
  );
};
